import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import styled from 'styled-components';
import {entities, serviceURL} from '../../../../../util/uiconstants';
import Modal from '../../../../Common/Modal/Modal';
import RelateItemList from '../../../../Common/RelateList/RelateItemList';
import * as utils from './ManageSectionObjects.utils';
import {labels, currentResource} from '../../../../../util/utils';
import env from '../../../../../util/env';
import _ from 'lodash';

const ManageSectionObjectsModal = (props) => {
	const {documentId, engagementId, onClose, show, section, documentEntityId} = props;
	const sectionRelatedObjects = useSelector((store) => {
		if (store.sectionRelatedObjects) {
			return store.sectionRelatedObjects[props.section.id];
		}
		return {};
	});

	const dispatch = useDispatch();

	const relatedObjectsData = React.useRef(
		_.cloneDeep(
			documentEntityId === entities.Control ? sectionRelatedObjects.newFsroTreeList : sectionRelatedObjects.fsroTreeList
		)
	);

	const [configurationInfo, setConfigurationInfo] = React.useState(null);

	React.useEffect(() => {
		if (documentEntityId) {
			setConfigurationInfo(utils.getConfigurationForDocumentEntityObject(documentEntityId));
		}
	}, []);

	const componentHeader = React.useCallback(() => {
		let sectionName =
			section && section.customLabel !== undefined && section.customLabel !== null
				? section.name + ' - ' + section.customLabel
				: section?.name;
		return configurationInfo.componentHeader(sectionName);
	}, [configurationInfo, section]);

	const data = React.useCallback(
		(page, pageSize, searchString, parentItem) => {
			if (!configurationInfo.supportsServerSidePagination) {
				if (!configurationInfo.urlRequired) {
					return dispatch(configurationInfo.fetchAction(engagementId, documentId));
				} else {
					let url =
						env.getBaseURL(serviceURL, engagementId) + 'Risks' + '?orderBy=RiskName&orderByAscending=true&pageSize=-1';
					return dispatch(configurationInfo.fetchAction(url));
				}
			} else {
				return dispatch(configurationInfo.fetchAction(engagementId, page, pageSize, searchString, parentItem));
			}
		},
		[configurationInfo]
	);

	const childrenData = React.useCallback(
		(__, ___, ____, parentItem) => {
			let expand;
			if (documentEntityId === entities.SCOT) {
				expand = parentItem.entityId === entities.SCOT ? 'wcgw,wcgwControl' : '';
			} else {
				expand = parentItem.entityId === entities.Control ? 'itapplications' : '';
			}
			return dispatch(configurationInfo.children.data(engagementId, parentItem.id, null, expand));
		},
		[configurationInfo]
	);

	const mapResponse = React.useCallback(
		(response) => {
			return configurationInfo.mapResponse(response, engagementId);
		},
		[configurationInfo]
	);

	const filter = React.useCallback(
		(element, searchString) => {
			return configurationInfo.filter(element, searchString);
		},
		[configurationInfo]
	);

	const onCheckChange = React.useCallback(
		(element, checked) => {
			let formSectionRelatedObjectId;

			if (checked === false) {
				formSectionRelatedObjectId = utils.findSectionRelatedObject(
					element.entityId,
					element.id,
					element.parentEntityId,
					element.parentEntityUId,
					relatedObjectsData.current
				);
			}

			return configurationInfo.onCheckChange(
				engagementId,
				documentId,
				section?.id,
				dispatch,
				element,
				checked,
				formSectionRelatedObjectId
			);
		},
		[configurationInfo]
	);

	const onLevel2CheckChange = React.useCallback(
		(element, checked) => {
			let formSectionRelatedObjectId;

			let level2Data = relatedObjectsData.current.find((ro) => {
				return Number(ro.entityUId) === element.parentEntityUId;
			}).children;

			if (checked === false) {
				if (level2Data) {
					formSectionRelatedObjectId = utils.findSectionRelatedObject(
						element.entityId,
						element.id,
						element.parentEntityId,
						element.parentEntityUId,
						level2Data
					);
				}
			}

			return configurationInfo.onCheckChange(
				engagementId,
				documentId,
				section?.id,
				dispatch,
				element,
				checked,
				formSectionRelatedObjectId
			);
		},
		[configurationInfo]
	);

	const onCheckChanged = React.useCallback(
		(item, value, hierarchy, response) => {
			console.log(item, value, hierarchy, response);

			const parentObject = findParent(relatedObjectsData.current, item, hierarchy);
			if (parentObject === undefined) {
				if (value === true) {
					relatedObjectsData.current.push({...response, children: []});
				} else {
					const index = relatedObjectsData.current.findIndex((element) => {
						return (
							Number(element.entityUId) === Number(item.id) &&
							element.entityId === item.entityId &&
							Number(element.parentEntityUId) === Number(item.parentEntityUId) &&
							element.parentEntityId === item.parentEntityId
						);
					});
					relatedObjectsData.current.splice(index, 1);
				}
			} else {
				if (value === true) {
					parentObject.children.push({...response, children: []});
				} else {
					const index = parentObject.children.findIndex((element) => {
						return (
							Number(element.entityUId) === Number(item.id) &&
							element.entityId === item.entityId &&
							Number(element.parentEntityUId) === Number(item.parentEntityUId) &&
							element.parentEntityId === item.parentEntityId
						);
					});
					parentObject.children.splice(index, 1);
				}
			}
		},
		[relatedObjectsData]
	);

	function findParent(state, object) {
		let parentObject;

		// Find object in the current level. If I cant find it, search for in in the children
		if (state && state.length > 0) {
			parentObject = state.find(
				(item) => item.entityId === object.parentEntityId && Number(item.entityUId) === Number(object.parentEntityUId)
			);

			if (!parentObject) {
				for (let i = 0; i < state.length; i++) {
					if (state[i].children) {
						parentObject = findParent(state[i].children, object);
					}
					if (parentObject) {
						break;
					}
				}
			}
		}

		return parentObject;
	}

	const render = React.useCallback(
		(item) => {
			return configurationInfo.render(item);
		},
		[configurationInfo]
	);

	const childrenSelected = React.useCallback(
		(element) => {
			if (configurationInfo.children.checkBox === undefined || configurationInfo.children.checkBox === true) {
				return configurationInfo.children.selected(relatedObjectsData.current, element);
			}
		},
		[configurationInfo]
	);

	const selected = React.useCallback(
		(element) => {
			if (configurationInfo.checkBox === undefined || configurationInfo.checkBox === true) {
				return configurationInfo.selected(relatedObjectsData.current, element);
			}
		},
		[configurationInfo]
	);

	const level2ChildrenSelected = React.useCallback(
		(element) => {
			if (
				configurationInfo.children.children.checkBox === undefined ||
				configurationInfo.children.children.checkBox === true
			) {
				let data = relatedObjectsData.current.find((ro) => {
					return Number(ro.entityUId) === element.parentEntityUId;
				});

				if (data) {
					return configurationInfo.children.children.selected(data.children, element);
				}
			}
		},
		[configurationInfo]
	);

	const children = React.useMemo(() => {
		if (configurationInfo?.children)
			return {
				checkBox: configurationInfo.children.checkBox,
				data: childrenData,
				filter: configurationInfo.children.filter,
				render: configurationInfo.children.render,
				mapResponse: configurationInfo.children.mapResponse,
				selected: childrenSelected,
				onCheckBoxChange: onCheckChange,
				eager: false,
				noResultsLabel: labels.noRelatedObjects,
				noDataLabel: labels.noRelatedObjects,
				onCheckBoxChanged: onCheckChanged,
				children: configurationInfo.children.children
					? {
							...configurationInfo.children.children,
							data: configurationInfo.children.children.data,
							filter: configurationInfo.children.children.filter,
							render: configurationInfo.children.children.render,
							mapResponse: configurationInfo.children.children.mapResponse,
							enabledByParent: configurationInfo.children.children.enabledByParent,
							selected: level2ChildrenSelected,
							onCheckBoxChange: onLevel2CheckChange,
							eager: false,
							noResultsLabel: labels.noRelatedObjects,
							noDataLabel: labels.noRelatedObjects,
							onCheckBoxChanged: onCheckChanged
					  }
					: null
			};
	}, [configurationInfo, onCheckChange]);

	const configuration = React.useMemo(() => {
		if (configurationInfo) {
			return {
				data: data,
				noResultsLabel: configurationInfo.noResultsLabel,
				noDataLabel: configurationInfo.noDataLabel,
				mapResponse: mapResponse,
				render: render,
				checkBox: configurationInfo.checkBox,
				selected: configurationInfo.checkBox ? selected : undefined,
				filter: filter,
				onCheckBoxChange: configurationInfo.checkBox ? onCheckChange : undefined,
				children: children,
				onCheckBoxChanged: onCheckChanged
			};
		}
	}, [data, mapResponse, render, selected, filter, onCheckChange, onCheckChanged, configurationInfo, children]);

	return (
		<Modal
			show={show}
			title={labels.manageObjects}
			showManageObjectsModal
			modalsize={'medium'}
			onHide={onClose}
			showButtons={false}
			closeBtnTitle={labels.cancelButtonLabel}
			closeTitle={labels.closeLabel}
			modalContainerClass="ManageSectionObjectsModalContainer"
			modalbodystyle="ManageSectionObjectsModalBody"
		>
			<StyledRelateList className="StyledRelateList">
				{configurationInfo && (
					<RelateItemList
						search
						showSelectedOnlyCheckBox={false}
						styleName="sectionRelatedObjectRisk"
						searchClearHoverText={labels.clearHoverText}
						searchHoverText={labels.searchPlaceholder}
						expandText={labels.expand}
						collapseText={labels.collapse}
						showSelectedOnlyText={labels.fo_showOnlyRelated}
						paginationIconHover={currentResource.pagingSvgHoverText}
						paginationShowText={labels.pagingShowtext}
						{...configurationInfo.relateListProperties}
						componentHeader={componentHeader}
						configuration={configuration}
					/>
				)}
			</StyledRelateList>
		</Modal>
	);
};

export default ManageSectionObjectsModal;

const StyledRelateList = styled.section`
	height: 100%;
	.RelateListContainers {
		.HeaderWrapper {
			align-items: unset;
			.RelatedTasksDocument {
				margin: var(--px-10) 0 0 0;
				h3 {
					font-size: var(--px-16);
					width: 100%;
					margin: 0;
				}
			}
			.relate-list-paging {
				span {
					font-size: var(--px-14);
				}
			}
			.relate-list-toolbar {
				margin: var(--px-15) 0 0 0;
				.RelateItemsListPagination {
					min-height: var(--px-46);
					display: flex;
				}
			}
			.StyledRelateItemsListHeader {
				.RelateItemsListHeaderSelectedOnly {
					max-width: var(--px-200);
					.RelateItemsListHeaderSelectedOnlyCheckbox {
						width: var(--px-175);
						margin-top: var(--px-6);
					}
					.motif-checkbox {
						.motif-checkbox-label {
							width: 85%;
						}
					}
				}
			}
		}
		.relate-list-header--control,
		.relate-list-header--itsoapp,
		.relate-list-header--scot {
			display: flex;
			width: 100%;
			.relate-list-header__item-label--control,
			.relate-list-header__item-label--itsoapp,
			.relate-list-header__item-label--scot {
				padding-left: var(--px-33);
				font-weight: bold;
			}
			.relate-list-header__counter--control,
			.relate-list-header__counter--itsoapp,
			.relate-list-header__counter--scot {
				margin-left: auto;
				font-weight: bold;
				padding-right: var(--px-5);
			}
		}
		.relate-list-body-wrapper {
			overflow-x: hidden;
			margin-bottom: var(--px-5);
			.relate-list-no-results {
				border-bottom: var(--px-1) solid var(--neutrals-300);
				display: flex;
				justify-content: center;
				width: calc(100% - var(--px-20));
			}
			.relate-item-list-labelTxt {
				width: 100%;
				max-width: 100%;
				line-height: var(--px-20);
				.manage-objects-item--risk {
					width: 100%;
					line-height: normal;
				}
				.manage-objects-item--control,
				.manage-objects-item--itsoapp,
				.manage-objects-item--scot,
				.manage-objects-item--wcgw {
					width: calc(100% - var(--px-20));
					padding-right: var(--px-20);
				}
				.manage-objects-item--wcgw,
				.manage-objects-item--control {
					line-height: normal;
					margin: var(--px-5) 0;
					padding: 0;
				}
				.custom-icon {
					svg {
						vertical-align: middle;
					}
				}
			}
			.RelateItemListItem {
				.relate-item-list-level1 {
					.relate-list-body-wrapper {
						.relate-item-list-item {
							align-items: flex-start;
							.relate-item-list-svg {
								cursor: pointer;
								margin-top: var(--px-3);
							}
							&.Expand {
								.relate-item-list-labelTxt {
									cursor: default;
								}
								.relate-item-list-loading-indicator {
									position: absolute;
									top: 30%;
									left: 50%;
								}
							}
						}
					}
				}
				.relate-item-list-level2 {
					padding-left: var(--px-65);
				}
			}
		}
		.relate-item-list-item {
			align-items: center;
			position: relative;
			margin: var(--px-2) var(--px-10);
			padding: var(--px-5) 0;
			.relateListCheckSpan {
				margin-top: var(--px-3);
				.motif-checkbox-label-wrap {
					width: 100%;
				}
			}
			.relate-item-list-labelTxt {
				cursor: pointer;
				margin-bottom: var(--px-2);
				.CountWrapper {
					background: var(--neutrals-900);
					color: var(--neutrals-00white);
					padding: var(--px-2) var(--px-10);
					font-size: var(--px-12);
					border-radius: var(--px-10);
					height: var(--px-20);
					display: inline-flex;
					line-height: normal;
					width: var(--px-30);
					justify-content: center;
					margin-left: var(--px-10);
				}
			}
			.relate-item-list-loading-indicator {
				margin: 0 0 0 var(--px-50);
				padding: 0;
				width: inherit;
				position: absolute;
				top: 30%;
				left: 50%;
			}
		}
		&.sectionRelatedObjectRisk {
			.HeaderWrapper {
				.instructionalText {
					width: 100%;
				}
			}
			.relate-item-list-item {
				align-items: flex-start;
				padding: var(--px-5) 0;
				.relate-item-list-svg {
					margin-top: 0;
					svg {
						fill: var(--neutrals-900);
					}
					&.Expand {
						margin-top: var(--px-2);
					}
				}
				.relate-item-list-labelTxt {
					.RiskRelatedIcon {
						display: inline-block;
						width: var(--px-40);
						cursor: default;
						.motif-icon {
							display: inline-flex;
						}
						svg {
							margin-top: var(--px-3);
							cursor: default;
							fill: var(--neutrals-900);
						}
						.RiskRelatedIconText {
							position: relative;
							top: calc(var(--px-1) * -1);
							font-size: var(--px-12);
							font-weight: bold;
						}
					}
					.manage-objects-item--risk {
						width: calc(100% - var(--px-40));
						margin-top: var(--px-3);
						cursor: default;
					}
				}
			}
			.RelateItemsListHeaderComponent {
				width: calc(100% - var(--px-250));
			}
			.RelateItemsListHeaderSelectedOnly {
				max-width: var(--px-250);
				width: var(--px-250);
				.RelateItemsListHeaderSelectedOnlyCheckbox {
					width: 100% !important;
					.motif-checkbox-label-wrap {
						justify-content: flex-end;
						display: flex;
						.motif-checkbox-input {
							position: relative;
							left: var(--px-17);
						}
						.motif-checkbox-label {
							width: auto;
							max-width: 85%;
						}
					}
				}
			}
		}
	}
`;
